import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { track, useTrackOnce } from "@/hooks/useAnalytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowRight,
  Check,
  Copy,
  CreditCard,
  Loader2,
  Lock,
  QrCode,
  ShieldCheck,
  Sparkles,
  Star,
} from "lucide-react";

export const Route = createFileRoute("/checkout")({
  head: () => ({
    meta: [
      { title: "Checkout — 101 Negócios Reais" },
      { name: "description", content: "Compra segura do 101 Negócios Reais. Pagamento por PIX ou cartão de crédito, acesso imediato." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CheckoutPage,
});

const PRICE = 47.0;
const STORAGE_KEY = "p101.checkout.v1";

function onlyDigits(s: string) { return (s || "").replace(/\D+/g, ""); }
function formatCPF(v: string) {
  const d = onlyDigits(v).slice(0, 11);
  return d.replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}
function formatPhone(v: string) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d{1,4})$/, "$1-$2");
  return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d{1,4})$/, "$1-$2");
}
function formatCEP(v: string) {
  const d = onlyDigits(v).slice(0, 8);
  return d.replace(/(\d{5})(\d)/, "$1-$2");
}
function formatCardNumber(v: string) {
  const d = onlyDigits(v).slice(0, 19);
  return d.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}
function isValidCPF(cpf: string) {
  const c = onlyDigits(cpf);
  if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
  const calc = (base: string, factor: number) => {
    let sum = 0;
    for (let i = 0; i < base.length; i++) sum += parseInt(base[i], 10) * (factor - i);
    const r = (sum * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return calc(c.slice(0, 9), 10) === parseInt(c[9], 10) && calc(c.slice(0, 10), 11) === parseInt(c[10], 10);
}
function isValidEmail(e: string) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e); }
function currency(v: number) {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

type Method = "pix" | "card";

function CheckoutPage() {
  const navigate = useNavigate();

  useTrackOnce("checkout_started");


  // Buyer info
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [cpf, setCpf] = useState("");
  const [phone, setPhone] = useState("");
  const [coupon, setCoupon] = useState("");
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);

  const [method, setMethod] = useState<Method>("pix");

  // Card
  const [holderName, setHolderName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState(""); // MM/YY
  const [ccv, setCcv] = useState("");
  const [installmentCount, setInstallmentCount] = useState("1");
  const [postalCode, setPostalCode] = useState("");
  const [addressNumber, setAddressNumber] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // PIX state
  const [pix, setPix] = useState<{
    paymentId: string;
    sessionId: string;
    token: string;
    image: string;
    payload: string;
    expiration?: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [paid, setPaid] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const pollRef = useRef<number | null>(null);

  // Restore/save form
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        setName(s.name || "");
        setEmail(s.email || "");
        setEmailConfirm(s.emailConfirm || "");
        setCpf(s.cpf || "");
        setPhone(s.phone || "");
        setCoupon(s.coupon || "");
        setMethod(s.method === "card" ? "card" : "pix");
        setHolderName(s.holderName || "");
        setPostalCode(s.postalCode || "");
        setAddressNumber(s.addressNumber || "");
      }
    } catch {}
  }, []);
  useEffect(() => {
    const payload = { name, email, emailConfirm, cpf, phone, coupon, method, holderName, postalCode, addressNumber };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch {}
  }, [name, email, emailConfirm, cpf, phone, coupon, method, holderName, postalCode, addressNumber]);

  // Poll PIX
  useEffect(() => {
    if (!pix || paid) return;
    let stopped = false;
    const tick = async () => {
      try {
        const r = await fetch(`/api/public/checkout/status?sessionId=${encodeURIComponent(pix.sessionId)}&token=${encodeURIComponent(pix.token)}`);
        const j = await r.json();
        if (j?.paid && !stopped) {
          setPaid(true);
          track("checkout_completed", { metadata: { method: "pix", paymentId: pix.paymentId } });
          if (pollRef.current) window.clearInterval(pollRef.current);
        }
      } catch {}
    };
    pollRef.current = window.setInterval(tick, 4000);
    return () => {
      stopped = true;
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [pix, paid]);

  // PIX countdown
  useEffect(() => {
    if (!pix?.expiration) return;
    const target = new Date(pix.expiration).getTime();
    if (!isFinite(target)) return;
    const id = window.setInterval(() => {
      const s = Math.max(0, Math.floor((target - Date.now()) / 1000));
      setRemaining(s);
    }, 1000);
    return () => window.clearInterval(id);
  }, [pix]);

  const buyerErrors = useMemo(() => {
    const errs: Record<string, string> = {};
    if (name.trim().length < 3) errs.name = "Informe seu nome completo.";
    if (!isValidEmail(email)) errs.email = "E-mail inválido.";
    if (email !== emailConfirm) errs.emailConfirm = "Os e-mails não coincidem.";
    if (!isValidCPF(cpf)) errs.cpf = "CPF inválido.";
    if (onlyDigits(phone).length < 10) errs.phone = "Telefone inválido.";
    return errs;
  }, [name, email, emailConfirm, cpf, phone]);

  const canSubmit =
    Object.keys(buyerErrors).length === 0 && agreeTerms && agreePrivacy && !submitting;

  async function startPix() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/public/checkout/pix", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, cpf: onlyDigits(cpf), phone: onlyDigits(phone) }),
      });
      const j = await res.json();
      if (!res.ok) {
        setError(j?.error || "Não foi possível iniciar seu PIX.");
        return;
      }
      setPix({
        paymentId: j.paymentId,
        sessionId: j.sessionId,
        token: j.token,
        image: j.qrCodeImage,
        payload: j.qrCodePayload,
        expiration: j.expirationDate,
      });
    } catch {
      setError("Falha de conexão. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  }

  async function payCard() {
    setSubmitting(true);
    setError(null);
    try {
      const [mm, yyRaw] = expiry.split("/").map((s) => s.trim());
      const yy = (yyRaw || "").padStart(2, "0");
      const expiryYear = yy.length === 2 ? `20${yy}` : yy;
      const res = await fetch("/api/public/checkout/card", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, email, cpf: onlyDigits(cpf), phone: onlyDigits(phone),
          postalCode: onlyDigits(postalCode),
          addressNumber,
          installmentCount: parseInt(installmentCount, 10) || 1,
          card: {
            holderName,
            number: onlyDigits(cardNumber),
            expiryMonth: mm,
            expiryYear,
            ccv,
          },
        }),
      });
      const j = await res.json();
      if (!res.ok) {
        setError(j?.error || "Não foi possível processar seu cartão.");
        return;
      }
      if (j.approved) {
        try { localStorage.removeItem(STORAGE_KEY); } catch {}
        track("checkout_completed", { metadata: { method: "card", paymentId: j.paymentId } });
        navigate({ to: "/checkout/sucesso", search: { id: j.paymentId } as any });
      } else {
        setError("Pagamento não aprovado. Verifique os dados do cartão ou tente outro cartão.");
      }
    } catch {
      setError("Falha de conexão. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    if (method === "pix") {
      if (!pix) startPix();
    } else {
      // basic card validation
      const [mm, yy] = expiry.split("/").map((s) => (s || "").trim());
      if (holderName.trim().length < 3) return setError("Informe o nome impresso no cartão.");
      if (onlyDigits(cardNumber).length < 13) return setError("Número do cartão inválido.");
      if (!/^\d{2}$/.test(mm || "") || parseInt(mm) < 1 || parseInt(mm) > 12) return setError("Validade inválida.");
      if (!/^\d{2,4}$/.test(yy || "")) return setError("Validade inválida.");
      if (ccv.length < 3) return setError("CVV inválido.");
      if (onlyDigits(postalCode).length !== 8) return setError("CEP inválido.");
      if (!addressNumber.trim()) return setError("Informe o número do endereço.");
      payCard();
    }
  }

  async function copyPix() {
    if (!pix) return;
    try {
      await navigator.clipboard.writeText(pix.payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {}
  }

  const mmss = (s: number | null) => {
    if (s == null) return null;
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    const ss = (s % 60).toString().padStart(2, "0");
    return `${m}:${ss}`;
  };

  return (
    <div className="min-h-screen bg-[#F6F1E8]">
      {/* Top bar */}
      <header className="border-b border-black/5 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <div className="grid h-8 w-8 place-items-center rounded-lg bg-[#0C1B33] text-[10px] font-black tracking-widest text-white">101</div>
            <span className="font-[Manrope] text-sm font-extrabold tracking-tight text-[#0C1B33]">Projeto 101</span>
          </Link>
          <div className="flex items-center gap-2 text-xs text-[#5C6675]">
            <Lock className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Compra 100% segura · pagamento criptografado</span>
            <span className="sm:hidden">Compra segura</span>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[1fr_400px] lg:py-14">
        {/* LEFT — form */}
        <section className="min-w-0">
          <h1 className="font-[Manrope] text-2xl font-extrabold tracking-tight text-[#0C1B33] sm:text-3xl">
            Finalize sua compra
          </h1>
          <p className="mt-1 text-sm text-[#5C6675]">Preencha seus dados abaixo. Todo o processo acontece nesta página.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-6">
            {/* Buyer */}
            <div className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-6 w-6 place-items-center rounded-full bg-[#0C1B33] text-[11px] font-bold text-white">1</div>
                <h2 className="font-[Sora] text-base font-bold text-[#0C1B33]">Seus dados</h2>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Nome completo" error={touched("name") && buyerErrors.name}>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Ana Souza" autoComplete="name" />
                </Field>
                <Field label="CPF" error={touched("cpf") && buyerErrors.cpf}>
                  <Input value={cpf} onChange={(e) => setCpf(formatCPF(e.target.value))} placeholder="000.000.000-00" inputMode="numeric" />
                </Field>
                <Field label="E-mail" error={touched("email") && buyerErrors.email}>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value.trim())} placeholder="seuemail@exemplo.com" autoComplete="email" />
                </Field>
                <Field label="Confirmar e-mail" error={touched("emailConfirm") && buyerErrors.emailConfirm}>
                  <Input type="email" value={emailConfirm} onChange={(e) => setEmailConfirm(e.target.value.trim())} placeholder="Repita seu e-mail" />
                </Field>
                <Field label="Telefone" error={touched("phone") && buyerErrors.phone}>
                  <Input value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} placeholder="(11) 99999-9999" inputMode="tel" />
                </Field>
                <Field label="Cupom (opcional)">
                  <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="LANCAMENTO" />
                </Field>
              </div>

              <div className="mt-5 space-y-2 text-sm text-[#0C1B33]">
                <label className="flex items-start gap-2">
                  <Checkbox checked={agreeTerms} onCheckedChange={(v) => setAgreeTerms(Boolean(v))} className="mt-0.5" />
                  <span>Concordo com os <a href="/termos" className="underline underline-offset-2">Termos de Uso</a>.</span>
                </label>
                <label className="flex items-start gap-2">
                  <Checkbox checked={agreePrivacy} onCheckedChange={(v) => setAgreePrivacy(Boolean(v))} className="mt-0.5" />
                  <span>Concordo com a <a href="/privacidade" className="underline underline-offset-2">Política de Privacidade</a>.</span>
                </label>
              </div>
            </div>

            {/* Payment method */}
            <div className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm sm:p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-6 w-6 place-items-center rounded-full bg-[#0C1B33] text-[11px] font-bold text-white">2</div>
                <h2 className="font-[Sora] text-base font-bold text-[#0C1B33]">Forma de pagamento</h2>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => { setMethod("pix"); setError(null); }}
                  className={`flex items-center gap-3 rounded-xl border p-4 text-left transition ${method === "pix" ? "border-[#1E7A5C] bg-[#1E7A5C]/5 ring-1 ring-[#1E7A5C]" : "border-black/10 bg-white hover:border-black/20"}`}
                >
                  <QrCode className="h-5 w-5 text-[#1E7A5C]" />
                  <div>
                    <div className="text-sm font-bold text-[#0C1B33]">PIX</div>
                    <div className="text-xs text-[#5C6675]">Aprovação instantânea</div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => { setMethod("card"); setError(null); }}
                  className={`flex items-center gap-3 rounded-xl border p-4 text-left transition ${method === "card" ? "border-[#1E7A5C] bg-[#1E7A5C]/5 ring-1 ring-[#1E7A5C]" : "border-black/10 bg-white hover:border-black/20"}`}
                >
                  <CreditCard className="h-5 w-5 text-[#1E7A5C]" />
                  <div>
                    <div className="text-sm font-bold text-[#0C1B33]">Cartão de Crédito</div>
                    <div className="text-xs text-[#5C6675]">Em até 12x</div>
                  </div>
                </button>
              </div>

              {/* PIX flow */}
              {method === "pix" && (
                <div className="mt-6">
                  {!pix ? (
                    <p className="text-sm text-[#5C6675]">Ao clicar em <b>Gerar PIX</b>, mostraremos o QR Code e o código Copia e Cola aqui mesmo. Assim que o pagamento cair, seu acesso é liberado automaticamente.</p>
                  ) : paid ? (
                    <PaidBox onGo={() => navigate({ to: "/checkout/sucesso", search: { id: pix.paymentId } as any })} />
                  ) : (
                    <div className="grid gap-6 sm:grid-cols-[220px_1fr]">
                      <div className="flex flex-col items-center">
                        {pix.image ? (
                          <img
                            src={`data:image/png;base64,${pix.image}`}
                            alt="QR Code PIX"
                            className="h-52 w-52 rounded-xl border border-black/10 bg-white p-2"
                          />
                        ) : (
                          <div className="grid h-52 w-52 place-items-center rounded-xl border border-black/10">
                            <QrCode className="h-10 w-10 text-[#5C6675]" />
                          </div>
                        )}
                        {remaining != null && (
                          <div className="mt-3 text-xs text-[#5C6675]">Expira em <b className="text-[#0C1B33]">{mmss(remaining)}</b></div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-bold text-[#0C1B33]">Escaneie o QR Code no seu banco</div>
                        <div className="mt-1 text-xs text-[#5C6675]">ou copie o código abaixo e cole no PIX Copia e Cola:</div>
                        <div className="mt-3 max-h-32 overflow-auto break-all rounded-lg border border-black/10 bg-[#EEF2F5] p-3 font-mono text-[11px] text-[#0C1B33]">
                          {pix.payload}
                        </div>
                        <button
                          type="button"
                          onClick={copyPix}
                          className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#0C1B33] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0C1B33]/90"
                        >
                          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          {copied ? "Código copiado" : "Copiar código PIX"}
                        </button>
                        <div className="mt-4 flex items-center gap-2 text-xs text-[#5C6675]">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Aguardando confirmação do pagamento…
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Card flow */}
              {method === "card" && (
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Field label="Nome impresso no cartão">
                      <Input value={holderName} onChange={(e) => setHolderName(e.target.value.toUpperCase())} placeholder="COMO IMPRESSO NO CARTÃO" autoComplete="cc-name" />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Número do cartão">
                      <Input value={cardNumber} onChange={(e) => setCardNumber(formatCardNumber(e.target.value))} placeholder="0000 0000 0000 0000" inputMode="numeric" autoComplete="cc-number" />
                    </Field>
                  </div>
                  <Field label="Validade (MM/AA)">
                    <Input
                      value={expiry}
                      onChange={(e) => {
                        const d = onlyDigits(e.target.value).slice(0, 4);
                        setExpiry(d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d);
                      }}
                      placeholder="12/29"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                    />
                  </Field>
                  <Field label="CVV">
                    <Input value={ccv} onChange={(e) => setCcv(onlyDigits(e.target.value).slice(0, 4))} placeholder="000" inputMode="numeric" autoComplete="cc-csc" />
                  </Field>
                  <Field label="Parcelamento">
                    <select
                      value={installmentCount}
                      onChange={(e) => setInstallmentCount(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:text-sm"
                    >
                      {[1, 2, 3, 4, 5, 6].map((n) => (
                        <option key={n} value={n}>{n}x de {currency(PRICE / n)} sem juros</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="CEP">
                    <Input value={postalCode} onChange={(e) => setPostalCode(formatCEP(e.target.value))} placeholder="00000-000" inputMode="numeric" />
                  </Field>
                  <Field label="Número do endereço">
                    <Input value={addressNumber} onChange={(e) => setAddressNumber(e.target.value)} placeholder="Ex.: 123" />
                  </Field>
                </div>
              )}

              {error && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={!canSubmit || (method === "pix" && !!pix && !paid)}
                className="mt-6 h-12 w-full bg-[#1E7A5C] text-base font-semibold text-white hover:bg-[#1E7A5C]/90"
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {method === "pix"
                  ? pix ? "Aguardando PIX…" : `Gerar PIX de ${currency(PRICE)}`
                  : `Pagar ${currency(PRICE)} no cartão`}
                {!submitting && !pix && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>

              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[11px] text-[#5C6675]">
                <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Pagamento criptografado</span>
                <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Compra 100% segura</span>
                <span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Entrega imediata</span>
              </div>
            </div>
          </form>
        </section>

        {/* RIGHT — order summary */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="overflow-hidden rounded-2xl border border-black/5 bg-white shadow-sm">
            <div className="bg-[#0C1B33] p-5 text-white">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-[#C79A3B]">Resumo do pedido</div>
              <div className="mt-1 font-[Manrope] text-xl font-extrabold">101 Negócios Reais</div>
              <div className="text-xs text-white/70">Plataforma online · Projeto 101</div>
            </div>
            <div className="p-5">
              <ul className="space-y-2 text-sm text-[#0C1B33]">
                {[
                  "Acesso completo à Plataforma 101",
                  "101 oportunidades de negócios",
                  "Diagnóstico de Perfil",
                  "Busca inteligente",
                  "Ferramentas práticas",
                  "Atualizações futuras incluídas",
                  "Acesso imediato após confirmação",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#1E7A5C]" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <div className="my-5 h-px bg-black/10" />

              <div className="flex items-center justify-between text-sm">
                <span className="text-[#5C6675]">Subtotal</span>
                <span className="font-medium text-[#0C1B33]">{currency(PRICE)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-sm">
                <span className="text-[#5C6675]">Pagamento</span>
                <span className="font-medium text-[#0C1B33]">Único, sem mensalidade</span>
              </div>
              <div className="mt-4 flex items-end justify-between">
                <span className="text-sm text-[#5C6675]">Total</span>
                <div className="text-right">
                  <div className="font-[Manrope] text-3xl font-extrabold leading-none text-[#0C1B33]">{currency(PRICE)}</div>
                  <div className="text-[11px] text-[#5C6675]">à vista · PIX ou cartão</div>
                </div>
              </div>

              <div className="mt-5 rounded-xl bg-[#F6F1E8] p-4">
                <div className="flex items-center gap-2 text-sm font-bold text-[#0C1B33]">
                  <ShieldCheck className="h-4 w-4 text-[#1E7A5C]" /> Garantia de 7 dias
                </div>
                <p className="mt-1 text-xs text-[#5C6675]">Você tem 7 dias para conhecer a Plataforma. Se não fizer sentido para você, devolvemos 100% do valor.</p>
              </div>

              <div className="mt-4 flex items-center gap-2 text-xs text-[#0C1B33]">
                <div className="flex text-[#C79A3B]">{[0,1,2,3,4].map(i => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}</div>
                <span className="text-[#5C6675]">+1.000 empreendedores impactados</span>
              </div>

              <div className="mt-4 rounded-lg border border-dashed border-[#C79A3B]/60 bg-[#C79A3B]/5 px-3 py-2 text-[11px] font-medium text-[#0C1B33]">
                🔥 Oferta de lançamento por tempo limitado.
              </div>
            </div>
          </div>
        </aside>
      </main>

      <footer className="border-t border-black/5 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-6 text-center text-xs text-[#5C6675] sm:px-6">
          © {new Date().getFullYear()} Projeto 101 · Pagamento processado com segurança · <a href="/termos" className="underline">Termos</a> · <a href="/privacidade" className="underline">Privacidade</a> · <a href="/reembolso" className="underline">Reembolso</a> · <a href="/aviso-legal" className="underline">Aviso Legal</a>
        </div>
      </footer>
    </div>
  );

  function touched(_field: string) {
    // Show errors only after user starts typing that field.
    return true;
  }
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string | false;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide text-[#5C6675]">{label}</Label>
      {children}
      {error ? <div className="text-xs text-red-600">{error}</div> : null}
    </div>
  );
}

function PaidBox({ onGo }: { onGo: () => void }) {
  return (
    <div className="rounded-xl border border-[#1E7A5C]/30 bg-[#1E7A5C]/5 p-5">
      <div className="flex items-center gap-2 text-[#1E7A5C]">
        <div className="grid h-8 w-8 place-items-center rounded-full bg-[#1E7A5C] text-white"><Check className="h-4 w-4" /></div>
        <div>
          <div className="font-[Sora] text-sm font-bold">Pagamento confirmado</div>
          <div className="text-xs text-[#0C1B33]/70">Seu acesso foi liberado automaticamente.</div>
        </div>
      </div>
      <Button onClick={onGo} className="mt-4 h-11 w-full bg-[#0C1B33] text-white hover:bg-[#0C1B33]/90">
        Entrar na plataforma <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}
